import {
  StreamLainCoachmarkCheckboxRow,
  StreamLainCoachmarkFooter,
  StreamLainCoachmarkMessage,
  StreamLainCoachmarkShell,
} from '@affine/core/components/coachmark';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import type {
  EmbeddingBadgeAction,
  EmbeddingBadgeStatus,
  EmbeddingBadgeViewModel,
  EmbeddingIndexReviewCurrentValue,
  EmbeddingIndexReviewField,
  EmbeddingIndexReviewLocator,
  EmbeddingIndexReviewSubjectSummary,
} from '@blocksuite/affine/shared/services';
import { useService } from '@toeverything/infra';
import { html, nothing, type TemplateResult } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EmbeddingBadge } from './embedding-badge-react';
import {
  fetchIndexReviewSubject,
  isPdfIndexReviewConflictError,
  regenerateIndexReviewField,
  saveIndexReviewField,
} from './pdf-index-review';
import * as styles from './styles.css';

export interface PdfSearchCoverageBadgeState {
  availability: 'loading' | 'available' | 'unavailable';
  textIndexed: boolean;
  textIndexStatus: 'idle' | 'pending' | 'failed';
  imageTextStatus:
    | 'manual'
    | 'pending'
    | 'partial'
    | 'complete'
    | 'failed'
    | 'disabled';
}

export interface PdfSearchCoveragePageReview {
  locator: EmbeddingIndexReviewLocator | null;
  currentPageIndex: number;
  pageCount: number;
  onPageSelect: (pageIndex: number) => void;
}

// The PDF search badge renders through the SAME shared (Lit) shell as the image
// and attachment badges (sl: badge-uniformity), so look + interaction can no
// longer drift. The PDF's two status axes (text layer + image OCR) are mapped
// onto the shared 5-state model for colour/icon and surfaced precisely in the
// popover rows.
export function PdfSearchCoverageBadge({
  state,
  onIndexText,
  onScanImageText,
  onCheckAgain,
  pageReview,
  disabled,
  hidden,
  className,
}: {
  state: PdfSearchCoverageBadgeState;
  onIndexText?: () => void;
  onScanImageText?: () => void;
  onCheckAgain?: () => void;
  pageReview?: PdfSearchCoveragePageReview;
  disabled?: boolean;
  hidden?: boolean;
  className?: string;
}) {
  const t = useI18n();
  const workspaceServer = useService(WorkspaceServerService);
  const serverBaseUrl = workspaceServer.server?.baseUrl ?? location.origin;
  const [expanded, setExpanded] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [fileReviewSummary, setFileReviewSummary] =
    useState<EmbeddingIndexReviewSubjectSummary | null>(null);
  const [pageReviewSummary, setPageReviewSummary] =
    useState<EmbeddingIndexReviewSubjectSummary | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewNotice, setReviewNotice] = useState<string | null>(null);
  const [reviewBusyField, setReviewBusyField] = useState<string | null>(null);
  const [reviewSavedField, setReviewSavedField] = useState<string | null>(null);
  const [reviewRegenerationOpenKey, setReviewRegenerationOpenKey] = useState<
    string | null
  >(null);
  const [fileDescriptionDraft, setFileDescriptionDraft] = useState('');
  const [fileDescriptionHint, setFileDescriptionHint] = useState('');
  const [fileSearchTermsDraft, setFileSearchTermsDraft] = useState('');
  const [fileSearchTermsHint, setFileSearchTermsHint] = useState('');
  const [pageDescriptionDraft, setPageDescriptionDraft] = useState('');
  const [pageDescriptionHint, setPageDescriptionHint] = useState('');
  const [pageSearchTermsDraft, setPageSearchTermsDraft] = useState('');
  const [pageSearchTermsHint, setPageSearchTermsHint] = useState('');
  const [pageTextDraft, setPageTextDraft] = useState('');
  const [pageTextHint, setPageTextHint] = useState('');

  useEffect(() => {
    if (!reviewSavedField) return;
    const timer = window.setTimeout(() => setReviewSavedField(null), 1600);
    return () => window.clearTimeout(timer);
  }, [reviewSavedField]);

  const complete = state.textIndexed && state.imageTextStatus === 'complete';
  const pending =
    state.textIndexStatus === 'pending' || state.imageTextStatus === 'pending';
  const failed =
    state.textIndexStatus === 'failed' || state.imageTextStatus === 'failed';
  const availabilityLoading = state.availability === 'loading';
  const unavailable = state.availability === 'unavailable';
  const isDisabled = Boolean(disabled) || state.imageTextStatus === 'disabled';

  const canIndexText =
    state.availability === 'available' &&
    Boolean(onIndexText) &&
    !state.textIndexed &&
    state.textIndexStatus !== 'pending';
  const canScan =
    state.availability === 'available' &&
    !disabled &&
    Boolean(onScanImageText) &&
    state.textIndexed &&
    (state.imageTextStatus === 'manual' ||
      state.imageTextStatus === 'partial' ||
      state.imageTextStatus === 'failed');

  // Map the two PDF axes onto the shared 5-state model (colour + icon).
  let status: EmbeddingBadgeStatus;
  if (unavailable) {
    status = 'unavailable';
  } else if (availabilityLoading) {
    status = 'loading';
  } else if (pending) {
    status = 'loading';
  } else if (failed) {
    status = 'failed';
  } else if (complete || (state.textIndexed && !canScan)) {
    // Fully indexed, or text-searchable with no further image scan available
    // (e.g. image OCR disabled) — searchable is the final state.
    status = 'searchable';
  } else if (canIndexText || canScan) {
    status = 'not-indexed';
  } else if (isDisabled) {
    status = 'disabled';
  } else {
    status = 'not-indexed';
  }

  const hoverLabel = (() => {
    switch (status) {
      case 'searchable':
        return t['com.streamlain.pdf.searchCoverage.status.ready']();
      case 'loading':
        return t['com.streamlain.pdf.searchCoverage.status.scanning']();
      case 'failed':
        return t['com.streamlain.pdf.searchCoverage.status.failed']();
      case 'unavailable':
        return t['com.streamlain.embedding.status.indexStatusUnavailable']();
      case 'disabled':
        return t['com.streamlain.pdf.searchCoverage.status.off']();
      case 'not-indexed':
        return state.textIndexed
          ? imageTextLabel(t, state.imageTextStatus)
          : t['com.streamlain.pdf.searchCoverage.text.notIndexed']();
    }
  })();

  const textRow = {
    label: t['com.streamlain.pdf.searchCoverage.details.textLayer'](),
    value:
      state.availability === 'unavailable'
        ? t['com.streamlain.embedding.status.indexStatusUnavailable']()
        : state.availability === 'loading'
          ? t['com.streamlain.embedding.status.indexStatusIndexing']()
          : state.textIndexed
            ? t['com.streamlain.pdf.searchCoverage.text.indexed']()
            : state.textIndexStatus === 'pending'
              ? t['com.streamlain.pdf.searchCoverage.text.indexing']()
              : t['com.streamlain.pdf.searchCoverage.text.notIndexed'](),
  };
  const imageRow = {
    label: t['com.streamlain.pdf.searchCoverage.imageText'](),
    value:
      state.availability === 'unavailable'
        ? t['com.streamlain.embedding.status.indexStatusUnavailable']()
        : state.availability === 'loading'
          ? t['com.streamlain.embedding.status.indexStatusIndexing']()
          : imageTextLabel(t, state.imageTextStatus),
  };

  let actionLabel: string | undefined;
  let onAction: ((event: MouseEvent) => void) | undefined;
  if (unavailable && onCheckAgain) {
    actionLabel = t['com.streamlain.embedding.status.checkAgain']();
    onAction = event => {
      event.stopPropagation();
      onCheckAgain();
    };
  } else if (canIndexText) {
    actionLabel = t['com.streamlain.embedding.status.clickToIndex']();
    onAction = event => {
      event.stopPropagation();
      onIndexText?.();
    };
  } else if (canScan) {
    actionLabel = imageTextActionLabel(t, state.imageTextStatus);
    onAction = event => {
      event.stopPropagation();
      onScanImageText?.();
    };
  }

  const pageReviewLocator = pageReview?.locator ?? null;
  const pageReviewLocatorKey = useMemo(
    () => (pageReviewLocator ? JSON.stringify(pageReviewLocator) : ''),
    [pageReviewLocator]
  );
  const fileReviewLocator = useMemo(
    () =>
      pageReviewLocator
        ? {
            ...pageReviewLocator,
            pageIndex: null,
          }
        : null,
    [pageReviewLocator]
  );
  const canOpenReview =
    state.availability === 'available' &&
    !availabilityLoading &&
    Boolean(pageReviewLocator) &&
    Boolean(fileReviewLocator) &&
    Boolean(pageReview) &&
    (pageReview?.pageCount ?? 0) > 0;

  const loadReview = useCallback(
    (options?: { silent?: boolean }) => {
      if (
        !pageReviewLocator ||
        !fileReviewLocator ||
        (!reviewOpen && !options?.silent)
      ) {
        return;
      }
      if (!options?.silent) {
        setReviewLoading(true);
        setReviewError(null);
      }
      void Promise.all([
        fetchIndexReviewSubject({ serverBaseUrl, locator: fileReviewLocator }),
        fetchIndexReviewSubject({ serverBaseUrl, locator: pageReviewLocator }),
      ])
        .then(([fileSummary, pageSummary]) => {
          setFileReviewSummary(fileSummary);
          setPageReviewSummary(pageSummary);
          if (!options?.silent) {
            setFileDescriptionDraft(
              currentValueText(fileSummary, 'visualDescription')
            );
            setFileSearchTermsDraft(
              currentValueText(fileSummary, 'searchTerms')
            );
            setPageDescriptionDraft(
              currentValueText(pageSummary, 'visualDescription')
            );
            setPageSearchTermsDraft(
              currentValueText(pageSummary, 'searchTerms')
            );
            setPageTextDraft(currentValueText(pageSummary, 'pageText'));
            setFileDescriptionHint('');
            setFileSearchTermsHint('');
            setPageDescriptionHint('');
            setPageSearchTermsHint('');
            setPageTextHint('');
          }
        })
        .catch(() => {
          if (!options?.silent) {
            setReviewError(
              t['com.streamlain.embedding.status.reviewLoadFailed']()
            );
          }
        })
        .finally(() => {
          if (!options?.silent) {
            setReviewLoading(false);
          }
        });
    },
    [fileReviewLocator, pageReviewLocator, reviewOpen, serverBaseUrl, t]
  );

  useEffect(() => {
    if (!canOpenReview || reviewOpen) return;
    loadReview({ silent: true });
  }, [canOpenReview, loadReview, pageReviewLocatorKey, reviewOpen]);

  useEffect(() => {
    if (!reviewOpen) return;
    setFileReviewSummary(null);
    setPageReviewSummary(null);
    setReviewNotice(null);
    loadReview();
  }, [loadReview, pageReviewLocatorKey, reviewOpen]);

  const openReview = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setExpanded(false);
    setReviewOpen(true);
  }, []);

  const closeReview = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setReviewOpen(false);
    setReviewRegenerationOpenKey(null);
  }, []);

  const handleReviewMutationError = useCallback(
    (error: unknown) => {
      if (isPdfIndexReviewConflictError(error)) {
        setReviewError(t['com.streamlain.embedding.status.reviewConflict']());
        loadReview();
        return;
      }
      setReviewError(t['com.streamlain.embedding.status.reviewLoadFailed']());
    },
    [loadReview, t]
  );

  const saveReviewFieldValue = useCallback(
    (input: {
      locator: EmbeddingIndexReviewLocator | null;
      summary: EmbeddingIndexReviewSubjectSummary | null;
      field: EmbeddingIndexReviewField;
      value: unknown;
      busyKey: string;
      fieldKey: string;
    }) => {
      if (!input.locator || reviewBusyField) return;
      setReviewBusyField(input.busyKey);
      setReviewNotice(null);
      setReviewError(null);
      void saveIndexReviewField({
        serverBaseUrl,
        locator: input.locator,
        field: input.field,
        value: input.value,
        expectedActiveVersionId:
          input.summary && currentValue(input.summary, input.field)?.id,
      })
        .then(() => {
          setReviewSavedField(input.fieldKey);
          loadReview();
        })
        .catch(error => {
          handleReviewMutationError(error);
        })
        .finally(() => {
          setReviewBusyField(null);
        });
    },
    [handleReviewMutationError, loadReview, reviewBusyField, serverBaseUrl]
  );

  const regenerateReviewFieldValue = useCallback(
    (input: {
      locator: EmbeddingIndexReviewLocator | null;
      summary: EmbeddingIndexReviewSubjectSummary | null;
      field: Exclude<EmbeddingIndexReviewField, 'manualSearchAdditions'>;
      userHint: string;
      busyKey: string;
      onQueued: () => void;
    }) => {
      const userHint = input.userHint.trim();
      if (!input.locator || !userHint || reviewBusyField) return;
      setReviewBusyField(input.busyKey);
      setReviewNotice(null);
      setReviewError(null);
      const active = input.summary && currentValue(input.summary, input.field);
      void regenerateIndexReviewField({
        serverBaseUrl,
        locator: input.locator,
        field: input.field,
        userHint,
        options: {
          confirmed: Boolean(active),
          expectedActiveVersionId: active?.id ?? null,
          mediaType: 'application/pdf',
        },
      })
        .then(result => {
          if (!result.queued) {
            setReviewError(
              t['com.streamlain.embedding.status.indexStatusUnavailable']()
            );
            return;
          }
          input.onQueued();
          setReviewNotice(t['com.streamlain.embedding.status.reviewQueued']());
        })
        .catch(error => {
          handleReviewMutationError(error);
        })
        .finally(() => {
          setReviewBusyField(null);
        });
    },
    [handleReviewMutationError, reviewBusyField, serverBaseUrl, t]
  );

  const saveFileSearchTerms = useCallback(() => {
    saveReviewFieldValue({
      locator: fileReviewLocator,
      summary: fileReviewSummary,
      field: 'searchTerms',
      value: parseSearchTermsDraft(fileSearchTermsDraft),
      busyKey: 'file:save:searchTerms',
      fieldKey: 'file:searchTerms',
    });
  }, [
    fileReviewLocator,
    fileReviewSummary,
    fileSearchTermsDraft,
    saveReviewFieldValue,
  ]);

  const saveFileDescription = useCallback(() => {
    saveReviewFieldValue({
      locator: fileReviewLocator,
      summary: fileReviewSummary,
      field: 'visualDescription',
      value: fileDescriptionDraft.trim(),
      busyKey: 'file:save:visualDescription',
      fieldKey: 'file:visualDescription',
    });
  }, [
    fileDescriptionDraft,
    fileReviewLocator,
    fileReviewSummary,
    saveReviewFieldValue,
  ]);

  const savePageSearchTerms = useCallback(() => {
    saveReviewFieldValue({
      locator: pageReviewLocator,
      summary: pageReviewSummary,
      field: 'searchTerms',
      value: parseSearchTermsDraft(pageSearchTermsDraft),
      busyKey: 'page:save:searchTerms',
      fieldKey: 'page:searchTerms',
    });
  }, [
    pageReviewLocator,
    pageReviewSummary,
    pageSearchTermsDraft,
    saveReviewFieldValue,
  ]);

  const savePageText = useCallback(() => {
    saveReviewFieldValue({
      locator: pageReviewLocator,
      summary: pageReviewSummary,
      field: 'pageText',
      value: pageTextDraft.trim(),
      busyKey: 'page:save:pageText',
      fieldKey: 'page:pageText',
    });
  }, [
    pageReviewLocator,
    pageReviewSummary,
    pageTextDraft,
    saveReviewFieldValue,
  ]);

  const savePageDescription = useCallback(() => {
    saveReviewFieldValue({
      locator: pageReviewLocator,
      summary: pageReviewSummary,
      field: 'visualDescription',
      value: pageDescriptionDraft.trim(),
      busyKey: 'page:save:visualDescription',
      fieldKey: 'page:visualDescription',
    });
  }, [
    pageDescriptionDraft,
    pageReviewLocator,
    pageReviewSummary,
    saveReviewFieldValue,
  ]);

  const regenerateFileDescription = useCallback(() => {
    regenerateReviewFieldValue({
      locator: fileReviewLocator,
      summary: fileReviewSummary,
      field: 'visualDescription',
      userHint: fileDescriptionHint,
      busyKey: 'file:regen:visualDescription',
      onQueued: () => {
        setFileDescriptionHint('');
        setReviewRegenerationOpenKey(null);
      },
    });
  }, [
    fileDescriptionHint,
    fileReviewLocator,
    fileReviewSummary,
    regenerateReviewFieldValue,
  ]);

  const regenerateFileSearchTerms = useCallback(() => {
    regenerateReviewFieldValue({
      locator: fileReviewLocator,
      summary: fileReviewSummary,
      field: 'searchTerms',
      userHint: fileSearchTermsHint,
      busyKey: 'file:regen:searchTerms',
      onQueued: () => {
        setFileSearchTermsHint('');
        setReviewRegenerationOpenKey(null);
      },
    });
  }, [
    fileReviewLocator,
    fileReviewSummary,
    fileSearchTermsHint,
    regenerateReviewFieldValue,
  ]);

  const regeneratePageDescription = useCallback(() => {
    regenerateReviewFieldValue({
      locator: pageReviewLocator,
      summary: pageReviewSummary,
      field: 'visualDescription',
      userHint: pageDescriptionHint,
      busyKey: 'page:regen:visualDescription',
      onQueued: () => {
        setPageDescriptionHint('');
        setReviewRegenerationOpenKey(null);
      },
    });
  }, [
    pageDescriptionHint,
    pageReviewLocator,
    pageReviewSummary,
    regenerateReviewFieldValue,
  ]);

  const regeneratePageSearchTerms = useCallback(() => {
    regenerateReviewFieldValue({
      locator: pageReviewLocator,
      summary: pageReviewSummary,
      field: 'searchTerms',
      userHint: pageSearchTermsHint,
      busyKey: 'page:regen:searchTerms',
      onQueued: () => {
        setPageSearchTermsHint('');
        setReviewRegenerationOpenKey(null);
      },
    });
  }, [
    pageReviewLocator,
    pageReviewSummary,
    pageSearchTermsHint,
    regenerateReviewFieldValue,
  ]);

  const regeneratePageText = useCallback(() => {
    regenerateReviewFieldValue({
      locator: pageReviewLocator,
      summary: pageReviewSummary,
      field: 'pageText',
      userHint: pageTextHint,
      busyKey: 'page:regen:pageText',
      onQueued: () => {
        setPageTextHint('');
        setReviewRegenerationOpenKey(null);
      },
    });
  }, [
    pageReviewLocator,
    pageReviewSummary,
    pageTextHint,
    regenerateReviewFieldValue,
  ]);

  const actions: EmbeddingBadgeAction[] = [];
  if (actionLabel && onAction) {
    actions.push({ label: actionLabel, onClick: onAction, kind: 'secondary' });
  }
  if (canOpenReview) {
    actions.push({
      label: t['com.streamlain.embedding.status.reviewDetails'](),
      onClick: openReview,
      kind: 'primary',
    });
  }

  const reviewPanel =
    reviewOpen && pageReview
      ? renderPdfPageReviewPanel({
          t,
          pageReview,
          fileSummary: fileReviewSummary,
          pageSummary: pageReviewSummary,
          state,
          loading: reviewLoading,
          error: reviewError,
          notice: reviewNotice,
          busy: reviewBusyField,
          savedField: reviewSavedField,
          fileDescriptionDraft,
          fileDescriptionHint,
          fileSearchTermsDraft,
          fileSearchTermsHint,
          pageDescriptionDraft,
          pageDescriptionHint,
          pageSearchTermsDraft,
          pageSearchTermsHint,
          pageTextDraft,
          pageTextHint,
          regenerationOpenKey: reviewRegenerationOpenKey,
          onRegenerationOpenChange: setReviewRegenerationOpenKey,
          onFileDescriptionDraftInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setFileDescriptionDraft(event.currentTarget.value);
              setReviewSavedField(current =>
                current === 'file:visualDescription' ? null : current
              );
            }
          },
          onFileDescriptionHintInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setFileDescriptionHint(event.currentTarget.value);
            }
          },
          onFileSearchTermsDraftInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setFileSearchTermsDraft(event.currentTarget.value);
              setReviewSavedField(current =>
                current === 'file:searchTerms' ? null : current
              );
            }
          },
          onFileSearchTermsHintInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setFileSearchTermsHint(event.currentTarget.value);
            }
          },
          onPageDescriptionDraftInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setPageDescriptionDraft(event.currentTarget.value);
              setReviewSavedField(current =>
                current === 'page:visualDescription' ? null : current
              );
            }
          },
          onPageDescriptionHintInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setPageDescriptionHint(event.currentTarget.value);
            }
          },
          onPageSearchTermsDraftInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setPageSearchTermsDraft(event.currentTarget.value);
              setReviewSavedField(current =>
                current === 'page:searchTerms' ? null : current
              );
            }
          },
          onPageSearchTermsHintInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setPageSearchTermsHint(event.currentTarget.value);
            }
          },
          onPageTextDraftInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setPageTextDraft(event.currentTarget.value);
              setReviewSavedField(current =>
                current === 'page:pageText' ? null : current
              );
            }
          },
          onPageTextHintInput: event => {
            if (event.currentTarget instanceof HTMLTextAreaElement) {
              setPageTextHint(event.currentTarget.value);
            }
          },
          onSaveFileDescription: saveFileDescription,
          onSaveFileSearchTerms: saveFileSearchTerms,
          onRegenerateFileDescription: regenerateFileDescription,
          onRegenerateFileSearchTerms: regenerateFileSearchTerms,
          onSavePageDescription: savePageDescription,
          onSavePageSearchTerms: savePageSearchTerms,
          onSavePageText: savePageText,
          onRegeneratePageDescription: regeneratePageDescription,
          onRegeneratePageSearchTerms: regeneratePageSearchTerms,
          onRegeneratePageText: regeneratePageText,
          onClose: closeReview,
        })
      : undefined;

  const vm: EmbeddingBadgeViewModel = {
    status,
    expanded: reviewOpen ? false : expanded,
    // PDF indexing has no per-chunk count → indeterminate ring (spins) while
    // loading; image/attachment badges show a determinate percentage.
    progress: null,
    hoverLabel,
    detailTitle: unavailable
      ? t['com.streamlain.embedding.status.indexStatusUnavailable']()
      : t['com.streamlain.pdf.searchCoverage.details.title'](),
    detailBody: unavailable
      ? t['com.streamlain.embedding.status.indexStatusUnavailableHint']()
      : undefined,
    actionLabel: actions.length === 0 ? actionLabel : undefined,
    onAction: actions.length === 0 ? onAction : undefined,
    actions,
    detailRows: [textRow, imageRow],
    reviewOpen,
    reviewPanel,
    onBadgeClick: event => {
      event.stopPropagation();
      if (reviewOpen) return;
      setExpanded(value => !value);
    },
  };

  if (hidden) return null;

  return (
    <EmbeddingBadge
      vm={vm}
      className={className ?? styles.pdfEmbeddingBadgeHostFloating}
      onOutsidePointerDown={
        reviewOpen
          ? () => {
              setReviewOpen(false);
              setReviewRegenerationOpenKey(null);
            }
          : undefined
      }
    />
  );
}

export function PdfSearchCoverageCoachmark({
  badgeState,
  dismissed,
  hidden,
  onDismissPermanently,
  open,
}: {
  badgeState: PdfSearchCoverageBadgeState;
  dismissed: boolean;
  hidden?: boolean;
  onDismissPermanently: () => Promise<void>;
  open: boolean;
}) {
  const t = useI18n();
  const [dismissedForSession, setDismissedForSession] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    setDismissedForSession(false);
    setDontShowAgain(false);
  }, [dismissed]);

  if (dismissed || dismissedForSession || hidden || !open) return null;

  return (
    <StreamLainCoachmarkShell className={styles.pdfSearchCoachmark}>
      <PdfSearchCoverageBadge
        state={badgeState}
        disabled
        className={styles.pdfEmbeddingBadgeHostInline}
      />
      <StreamLainCoachmarkMessage
        icon={null}
        body={t['com.streamlain.pdf.searchCoverage.coachmark.body']()}
      />
      <StreamLainCoachmarkFooter>
        <StreamLainCoachmarkCheckboxRow
          checked={dontShowAgain}
          label={t[
            'com.streamlain.pdf.searchCoverage.coachmark.dontShowAgain'
          ]()}
          onChange={(_, checked) => setDontShowAgain(checked)}
        />
        <button
          type="button"
          className={styles.pdfSearchCoverageAction}
          onClick={event => {
            event.stopPropagation();
            if (dontShowAgain) {
              void onDismissPermanently();
            }
            setDismissedForSession(true);
          }}
        >
          {t['Got it']()}
        </button>
      </StreamLainCoachmarkFooter>
    </StreamLainCoachmarkShell>
  );
}

function imageTextActionLabel(
  t: ReturnType<typeof useI18n>,
  state: PdfSearchCoverageBadgeState['imageTextStatus']
) {
  if (state === 'failed') {
    return t['com.streamlain.pdf.searchCoverage.action.retry']();
  }
  if (state === 'partial') {
    return t['com.streamlain.pdf.searchCoverage.action.continue']();
  }
  return t['com.streamlain.pdf.searchCoverage.action.scan']();
}

function imageTextLabel(
  t: ReturnType<typeof useI18n>,
  state: PdfSearchCoverageBadgeState['imageTextStatus']
) {
  switch (state) {
    case 'complete':
      return t['com.streamlain.pdf.searchCoverage.status.ready']();
    case 'pending':
      return t['com.streamlain.pdf.searchCoverage.status.scanning']();
    case 'partial':
      return t['com.streamlain.pdf.searchCoverage.status.partial']();
    case 'failed':
      return t['com.streamlain.pdf.searchCoverage.status.failed']();
    case 'disabled':
      return t['com.streamlain.pdf.searchCoverage.status.off']();
    case 'manual':
      return t['com.streamlain.pdf.searchCoverage.status.manual']();
  }
}

function renderPdfPageReviewPanel(input: {
  t: ReturnType<typeof useI18n>;
  pageReview: PdfSearchCoveragePageReview;
  fileSummary: EmbeddingIndexReviewSubjectSummary | null;
  pageSummary: EmbeddingIndexReviewSubjectSummary | null;
  state: PdfSearchCoverageBadgeState;
  loading: boolean;
  error: string | null;
  notice: string | null;
  busy: string | null;
  savedField: string | null;
  fileDescriptionDraft: string;
  fileDescriptionHint: string;
  fileSearchTermsDraft: string;
  fileSearchTermsHint: string;
  pageDescriptionDraft: string;
  pageDescriptionHint: string;
  pageSearchTermsDraft: string;
  pageSearchTermsHint: string;
  pageTextDraft: string;
  pageTextHint: string;
  regenerationOpenKey: string | null;
  onRegenerationOpenChange: (key: string | null) => void;
  onFileDescriptionDraftInput: (event: InputEvent) => void;
  onFileDescriptionHintInput: (event: InputEvent) => void;
  onFileSearchTermsDraftInput: (event: InputEvent) => void;
  onFileSearchTermsHintInput: (event: InputEvent) => void;
  onPageDescriptionDraftInput: (event: InputEvent) => void;
  onPageDescriptionHintInput: (event: InputEvent) => void;
  onPageSearchTermsDraftInput: (event: InputEvent) => void;
  onPageSearchTermsHintInput: (event: InputEvent) => void;
  onPageTextDraftInput: (event: InputEvent) => void;
  onPageTextHintInput: (event: InputEvent) => void;
  onSaveFileDescription: () => void;
  onSaveFileSearchTerms: () => void;
  onRegenerateFileDescription: () => void;
  onRegenerateFileSearchTerms: () => void;
  onSavePageDescription: () => void;
  onSavePageSearchTerms: () => void;
  onSavePageText: () => void;
  onRegeneratePageDescription: () => void;
  onRegeneratePageSearchTerms: () => void;
  onRegeneratePageText: () => void;
  onClose: (event: MouseEvent) => void;
}): TemplateResult {
  const {
    t,
    pageReview,
    fileSummary,
    pageSummary,
    state,
    loading,
    error,
    notice,
    busy,
    savedField,
    fileDescriptionDraft,
    fileDescriptionHint,
    fileSearchTermsDraft,
    fileSearchTermsHint,
    pageDescriptionDraft,
    pageDescriptionHint,
    pageSearchTermsDraft,
    pageSearchTermsHint,
    pageTextDraft,
    pageTextHint,
  } = input;
  const fileCanWrite = fileSummary?.canWrite === true;
  const pageCanWrite = pageSummary?.canWrite === true;
  const current = pageReview.currentPageIndex + 1;
  const total = pageReview.pageCount;

  return html`
    <div class="affine-embedding-badge-review-panel" @click=${stopReviewEvent}>
      <div class="affine-embedding-badge-review-header">
        <div class="affine-embedding-badge-review-title">
          ${t['com.streamlain.embedding.status.reviewPanelTitle']()}
        </div>
        <button
          type="button"
          class="affine-embedding-badge-review-close"
          @click=${input.onClose}
        >
          ${t['com.streamlain.embedding.status.reviewPanelClose']()}
        </button>
      </div>

      <div class="affine-embedding-badge-review-actions">
        <button
          type="button"
          class="affine-embedding-badge-review-button"
          ?disabled=${pageReview.currentPageIndex <= 0}
          aria-label=${t[
            'com.streamlain.pdf.searchCoverage.review.previousPage'
          ]()}
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            pageReview.onPageSelect(pageReview.currentPageIndex - 1);
          }}
        >
          ${'<'}
        </button>
        <strong>
          ${t['com.streamlain.pdf.searchCoverage.review.pagePosition']({
            current: String(current),
            total: String(total),
          })}
        </strong>
        <button
          type="button"
          class="affine-embedding-badge-review-button"
          ?disabled=${pageReview.currentPageIndex >= total - 1}
          aria-label=${t['com.streamlain.pdf.searchCoverage.review.nextPage']()}
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            pageReview.onPageSelect(pageReview.currentPageIndex + 1);
          }}
        >
          ${'>'}
        </button>
      </div>

      <div class="affine-embedding-badge-review-muted">
        ${t['com.streamlain.pdf.searchCoverage.review.pageStatus']()}:
        ${pdfPageStatusLabel(t, state, pageSummary)}
      </div>

      ${loading && (!fileSummary || !pageSummary)
        ? html`<div class="affine-embedding-badge-review-muted">
            ${t['com.streamlain.embedding.status.indexStatusIndexing']()}
          </div>`
        : nothing}
      ${error
        ? html`<div class="affine-embedding-badge-review-muted">${error}</div>`
        : nothing}
      ${notice
        ? html`<div class="affine-embedding-badge-review-muted">${notice}</div>`
        : nothing}
      ${fileSummary && pageSummary
        ? html`
            <section class="affine-embedding-badge-review-section">
              <div class="affine-embedding-badge-review-section-header">
                <div class="affine-embedding-badge-review-section-title">
                  ${t['com.streamlain.embedding.status.reviewScopeFile']()}
                </div>
                ${fileCanWrite
                  ? nothing
                  : html`<span class="affine-embedding-badge-review-muted">
                      ${t['com.streamlain.embedding.status.reviewReadOnly']()}
                    </span>`}
              </div>
              ${renderPdfReviewField({
                fieldKey: 'file:visualDescription',
                regenerationOpenKey: input.regenerationOpenKey,
                onRegenerationOpenChange: input.onRegenerationOpenChange,
                t,
                label:
                  t[
                    'com.streamlain.embedding.status.reviewSectionDescription'
                  ](),
                canWrite: fileCanWrite,
                draft: fileDescriptionDraft,
                originalValue: currentValueText(
                  fileSummary,
                  'visualDescription'
                ),
                saved: savedField === 'file:visualDescription',
                onDraftInput: input.onFileDescriptionDraftInput,
                saveBusy: busy === 'file:save:visualDescription',
                busy,
                onSave: input.onSaveFileDescription,
                hint: fileDescriptionHint,
                onHintInput: input.onFileDescriptionHintInput,
                regenerateBusy: busy === 'file:regen:visualDescription',
                onRegenerate: input.onRegenerateFileDescription,
              })}
              ${renderPdfReviewField({
                fieldKey: 'file:searchTerms',
                regenerationOpenKey: input.regenerationOpenKey,
                onRegenerationOpenChange: input.onRegenerationOpenChange,
                t,
                label:
                  t[
                    'com.streamlain.embedding.status.reviewSectionSearchTerms'
                  ](),
                canWrite: fileCanWrite,
                draft: fileSearchTermsDraft,
                originalValue: currentValueText(fileSummary, 'searchTerms'),
                saved: savedField === 'file:searchTerms',
                onDraftInput: input.onFileSearchTermsDraftInput,
                saveBusy: busy === 'file:save:searchTerms',
                busy,
                onSave: input.onSaveFileSearchTerms,
                hint: fileSearchTermsHint,
                onHintInput: input.onFileSearchTermsHintInput,
                regenerateBusy: busy === 'file:regen:searchTerms',
                onRegenerate: input.onRegenerateFileSearchTerms,
              })}
            </section>

            <section class="affine-embedding-badge-review-section">
              <div class="affine-embedding-badge-review-section-header">
                <div class="affine-embedding-badge-review-section-title">
                  ${t['com.streamlain.embedding.status.reviewScopePage']()}
                </div>
                ${pageCanWrite
                  ? nothing
                  : html`<span class="affine-embedding-badge-review-muted">
                      ${t['com.streamlain.embedding.status.reviewReadOnly']()}
                    </span>`}
              </div>

              ${renderPdfReviewField({
                fieldKey: 'page:visualDescription',
                regenerationOpenKey: input.regenerationOpenKey,
                onRegenerationOpenChange: input.onRegenerationOpenChange,
                t,
                label:
                  t[
                    'com.streamlain.embedding.status.reviewSectionDescription'
                  ](),
                canWrite: pageCanWrite,
                draft: pageDescriptionDraft,
                originalValue: currentValueText(
                  pageSummary,
                  'visualDescription'
                ),
                saved: savedField === 'page:visualDescription',
                onDraftInput: input.onPageDescriptionDraftInput,
                saveBusy: busy === 'page:save:visualDescription',
                busy,
                onSave: input.onSavePageDescription,
                hint: pageDescriptionHint,
                onHintInput: input.onPageDescriptionHintInput,
                regenerateBusy: busy === 'page:regen:visualDescription',
                onRegenerate: input.onRegeneratePageDescription,
              })}
              ${renderPdfReviewField({
                fieldKey: 'page:pageText',
                regenerationOpenKey: input.regenerationOpenKey,
                onRegenerationOpenChange: input.onRegenerationOpenChange,
                t,
                label:
                  t[
                    'com.streamlain.embedding.status.reviewSectionExtractedText'
                  ](),
                canWrite: pageCanWrite,
                draft: pageTextDraft,
                originalValue: currentValueText(pageSummary, 'pageText'),
                saved: savedField === 'page:pageText',
                onDraftInput: input.onPageTextDraftInput,
                saveBusy: busy === 'page:save:pageText',
                busy,
                onSave: input.onSavePageText,
                hint: pageTextHint,
                onHintInput: input.onPageTextHintInput,
                regenerateBusy: busy === 'page:regen:pageText',
                onRegenerate: input.onRegeneratePageText,
              })}
              ${renderPdfReviewField({
                fieldKey: 'page:searchTerms',
                regenerationOpenKey: input.regenerationOpenKey,
                onRegenerationOpenChange: input.onRegenerationOpenChange,
                t,
                label:
                  t[
                    'com.streamlain.embedding.status.reviewSectionSearchTerms'
                  ](),
                canWrite: pageCanWrite,
                draft: pageSearchTermsDraft,
                originalValue: currentValueText(pageSummary, 'searchTerms'),
                saved: savedField === 'page:searchTerms',
                onDraftInput: input.onPageSearchTermsDraftInput,
                saveBusy: busy === 'page:save:searchTerms',
                busy,
                onSave: input.onSavePageSearchTerms,
                hint: pageSearchTermsHint,
                onHintInput: input.onPageSearchTermsHintInput,
                regenerateBusy: busy === 'page:regen:searchTerms',
                onRegenerate: input.onRegeneratePageSearchTerms,
              })}
            </section>
          `
        : nothing}
    </div>
  `;
}

function stopReviewEvent(event: Event) {
  event.stopPropagation();
}

function autoSizeReviewTextarea(element: Element | null | undefined) {
  if (!(element instanceof HTMLTextAreaElement)) return;
  element.style.height = 'auto';
  const maxHeight = Number.parseFloat(getComputedStyle(element).maxHeight);
  const nextHeight = Number.isFinite(maxHeight)
    ? Math.min(element.scrollHeight, maxHeight)
    : element.scrollHeight;
  element.style.height = `${nextHeight}px`;
  element.style.overflowY =
    Number.isFinite(maxHeight) && element.scrollHeight > maxHeight
      ? 'auto'
      : 'hidden';
}

function renderPdfReviewField(input: {
  fieldKey: string;
  regenerationOpenKey: string | null;
  onRegenerationOpenChange: (key: string | null) => void;
  t: ReturnType<typeof useI18n>;
  label: string;
  canWrite: boolean;
  draft: string;
  originalValue: string;
  saved: boolean;
  onDraftInput: (event: InputEvent) => void;
  busy: string | null;
  saveBusy: boolean;
  onSave: () => void;
  hint: string;
  onHintInput: (event: InputEvent) => void;
  regenerateBusy: boolean;
  onRegenerate: () => void;
}): TemplateResult {
  const regenerationOpen = input.regenerationOpenKey === input.fieldKey;
  const dirty = input.draft !== input.originalValue;
  return html`
    <div class="affine-embedding-badge-review-editor">
      <div class="affine-embedding-badge-review-label">${input.label}</div>
      <textarea
        class="affine-embedding-badge-review-textarea"
        ${ref(autoSizeReviewTextarea)}
        .value=${input.draft}
        ?readonly=${!input.canWrite}
        @input=${(event: InputEvent) => {
          autoSizeReviewTextarea(event.currentTarget as HTMLTextAreaElement);
          input.onDraftInput(event);
        }}
      ></textarea>
      ${input.canWrite
        ? html`
            <div
              class="affine-embedding-badge-review-actions affine-embedding-badge-review-main-actions"
            >
              <button
                type="button"
                class="affine-embedding-badge-review-button ghost"
                aria-expanded=${regenerationOpen ? 'true' : 'false'}
                ?disabled=${input.busy !== null}
                @click=${() => input.onRegenerationOpenChange(input.fieldKey)}
              >
                <span aria-hidden="true">✦</span>
                ${input.t[
                  'com.streamlain.embedding.status.reviewRegenerateWithHint'
                ]()}
              </button>
              <button
                type="button"
                class=${input.saved
                  ? 'affine-embedding-badge-review-button saved'
                  : 'affine-embedding-badge-review-button primary'}
                ?disabled=${input.busy !== null || !dirty || input.saved}
                @click=${input.onSave}
              >
                ${input.saveBusy
                  ? input.t['com.streamlain.embedding.status.reviewSaving']()
                  : input.saved
                    ? `✓ ${input.t[
                        'com.streamlain.embedding.status.reviewSaved'
                      ]()}`
                    : input.t['com.streamlain.embedding.status.reviewSave']()}
              </button>
            </div>
            ${regenerationOpen
              ? html`<div class="affine-embedding-badge-review-regenerate">
                  <div class="affine-embedding-badge-review-regenerate-body">
                    <textarea
                      class="affine-embedding-badge-review-textarea affine-embedding-badge-review-hint"
                      ${ref(autoSizeReviewTextarea)}
                      placeholder=${input.t[
                        'com.streamlain.embedding.status.reviewHintPlaceholder'
                      ]()}
                      .value=${input.hint}
                      @input=${(event: InputEvent) => {
                        autoSizeReviewTextarea(
                          event.currentTarget as HTMLTextAreaElement
                        );
                        input.onHintInput(event);
                      }}
                    ></textarea>
                    <div class="affine-embedding-badge-review-muted">
                      ${input.t[
                        'com.streamlain.embedding.status.reviewReplaceWarning'
                      ]()}
                    </div>
                    <div
                      class="affine-embedding-badge-review-actions affine-embedding-badge-review-regenerate-actions"
                    >
                      <button
                        type="button"
                        class="affine-embedding-badge-review-button"
                        ?disabled=${input.busy !== null}
                        @click=${() => input.onRegenerationOpenChange(null)}
                      >
                        ${input.t[
                          'com.streamlain.embedding.status.reviewCancel'
                        ]()}
                      </button>
                      <button
                        type="button"
                        class="affine-embedding-badge-review-button primary"
                        ?disabled=${input.busy !== null || !input.hint.trim()}
                        @click=${input.onRegenerate}
                      >
                        ${input.regenerateBusy
                          ? input.t[
                              'com.streamlain.embedding.status.reviewRegenerating'
                            ]()
                          : input.t[
                              'com.streamlain.embedding.status.reviewRegenerateAction'
                            ]()}
                      </button>
                    </div>
                  </div>
                </div>`
              : nothing}
          `
        : nothing}
    </div>
  `;
}

function currentValue(
  summary: EmbeddingIndexReviewSubjectSummary,
  field: EmbeddingIndexReviewField
): EmbeddingIndexReviewCurrentValue | null {
  const pageIndex = summary.locator.pageIndex ?? null;
  return (
    summary.currentValues.find(
      version => version.field === field && version.pageIndex === pageIndex
    ) ?? null
  );
}

function currentValueText(
  summary: EmbeddingIndexReviewSubjectSummary,
  field: EmbeddingIndexReviewField
): string {
  return versionValueToText(field, currentValue(summary, field));
}

function versionValueToText(
  field: EmbeddingIndexReviewField,
  version: EmbeddingIndexReviewCurrentValue | null
): string {
  if (!version) return '';
  if (field === 'searchTerms') return formatSearchTerms(version.value);
  if (field === 'manualSearchAdditions') return formatStringList(version.value);
  return typeof version.value === 'string' ? version.value : '';
}

function formatSearchTerms(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  return Object.entries(value as Record<string, unknown>)
    .flatMap(([locale, terms]) => {
      if (!Array.isArray(terms)) return [];
      const normalizedTerms = terms.filter(
        (term): term is string => typeof term === 'string' && term.trim() !== ''
      );
      if (!normalizedTerms.length) return [];
      return `${locale}: ${normalizedTerms.join(', ')}`;
    })
    .join('\n');
}

function formatStringList(value: unknown): string {
  if (!Array.isArray(value)) return '';
  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .join('\n');
}

function parseSearchTermsDraft(draft: string): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const rawLine of draft.split(/\n/u)) {
    const line = rawLine.trim();
    if (!line) continue;
    const separatorIndex = line.indexOf(':');
    const locale =
      separatorIndex > 0 ? line.slice(0, separatorIndex).trim() : 'und';
    const normalizedLocale = locale.toLocaleLowerCase();
    const rawTerms = separatorIndex > 0 ? line.slice(separatorIndex + 1) : line;
    const terms = rawTerms
      .split(',')
      .map(term => term.trim())
      .filter(Boolean);
    if (terms.length) {
      result[normalizedLocale] = Array.from(
        new Set([...(result[normalizedLocale] ?? []), ...terms])
      );
    }
  }
  return result;
}

function pdfPageStatusLabel(
  t: ReturnType<typeof useI18n>,
  state: PdfSearchCoverageBadgeState,
  summary: EmbeddingIndexReviewSubjectSummary | null
): string {
  if (state.availability === 'unavailable') {
    return t['com.streamlain.embedding.status.indexStatusUnavailable']();
  }
  if (
    state.availability === 'loading' ||
    state.textIndexStatus === 'pending' ||
    state.imageTextStatus === 'pending'
  ) {
    return t['com.streamlain.embedding.status.indexStatusIndexing']();
  }
  if (!summary) {
    return t['com.streamlain.embedding.status.indexStatusIndexing']();
  }
  if (currentValueText(summary, 'pageText').trim()) {
    return t['com.streamlain.embedding.status.indexStatusIndexed']();
  }
  return t['com.streamlain.embedding.status.indexStatusNotIndexed']();
}
